import { useState, useEffect } from 'react';
import { X, Truck, User, MapPin, Check, Info } from 'lucide-react';
import type { DeliveryInfo } from '../types/plv.types';

interface DeliveryFormProps {
  defaultDeliveryData: DeliveryInfo;
  onConfirm: (deliveryData: DeliveryInfo) => void;
  onCancel: () => void;
}

export function DeliveryForm({ defaultDeliveryData, onConfirm, onCancel }: DeliveryFormProps) {
  const [deliveryData, setDeliveryData] = useState<DeliveryInfo>(defaultDeliveryData);
  const [errors, setErrors] = useState<Partial<Record<keyof DeliveryInfo, string>>>({});

  useEffect(() => {
    setDeliveryData(defaultDeliveryData);
  }, [defaultDeliveryData]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof DeliveryInfo, string>> = {};

    if (!deliveryData.contactLastname.trim()) newErrors.contactLastname = 'Le nom du contact est obligatoire';
    if (!deliveryData.contactFirstname.trim()) newErrors.contactFirstname = 'Le prénom du contact est obligatoire';
    if (!deliveryData.contactEmail.trim()) {
      newErrors.contactEmail = "L'email du contact est obligatoire";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(deliveryData.contactEmail.trim())) {
      newErrors.contactEmail = "L'email doit être valide";
    }
    if (!deliveryData.contactPhone.trim()) newErrors.contactPhone = 'Le téléphone du contact est obligatoire';
    if (!deliveryData.companyName.trim()) newErrors.companyName = 'Le nom de la société est obligatoire';
    if (!deliveryData.address.trim()) newErrors.address = "L'adresse est obligatoire";
    if (!deliveryData.postalCode.trim()) {
      newErrors.postalCode = 'Le code postal est obligatoire';
    } else if (!/^[0-9]{5}$/.test(deliveryData.postalCode.trim())) {
      newErrors.postalCode = 'Le code postal doit contenir 5 chiffres';
    }
    if (!deliveryData.city.trim()) newErrors.city = 'La ville est obligatoire';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof DeliveryInfo, value: string) => {
    setDeliveryData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) onConfirm(deliveryData);
  };

  const inputClass = (field: keyof DeliveryInfo) =>
    `tw-w-full tw-rounded-lg tw-border tw-px-3 tw-py-2 tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-affinicar-secondary focus:tw-border-affinicar-secondary ${
      errors[field] ? 'tw-border-red-400' : 'tw-border-gray-300'
    }`;

  return (
    <div
      className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-start tw-justify-center tw-bg-black/50 tw-overflow-y-auto tw-py-8"
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-2xl tw-mx-4"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-border-b tw-border-gray-200">
          <h5 className="tw-text-base tw-font-semibold tw-flex tw-items-center tw-gap-2">
            <Truck className="tw-w-5 tw-h-5 tw-text-affinicar-secondary" />
            Informations de contact et livraison
          </h5>
          <button className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400 hover:tw-text-gray-600" onClick={onCancel}>
            <X className="tw-w-5 tw-h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="tw-px-6 tw-py-4 tw-space-y-5">
            {/* Info */}
            <div className="tw-flex tw-items-start tw-gap-2 tw-p-3 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-text-sm tw-text-blue-700">
              <Info className="tw-w-4 tw-h-4 tw-mt-0.5 tw-flex-shrink-0" />
              <span>Les champs marqués d'un <span className="tw-text-red-500">*</span> sont obligatoires.</span>
            </div>

            {/* Contact */}
            <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
              <div className="tw-bg-gray-50 tw-px-4 tw-py-2.5 tw-border-b tw-border-gray-200">
                <h6 className="tw-text-sm tw-font-medium tw-flex tw-items-center tw-gap-2 tw-text-gray-600">
                  <User className="tw-w-4 tw-h-4" />
                  Contact de livraison
                </h6>
              </div>
              <div className="tw-p-4 tw-space-y-3">
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                  <div>
                    <label className="tw-text-xs tw-text-gray-500 tw-mb-1 tw-block">Nom <span className="tw-text-red-500">*</span></label>
                    <input type="text" className={inputClass('contactLastname')} value={deliveryData.contactLastname} onChange={e => handleInputChange('contactLastname', e.target.value)} placeholder="Nom du contact" />
                    {errors.contactLastname && <p className="tw-text-xs tw-text-red-500 tw-mt-1">{errors.contactLastname}</p>}
                  </div>
                  <div>
                    <label className="tw-text-xs tw-text-gray-500 tw-mb-1 tw-block">Prénom <span className="tw-text-red-500">*</span></label>
                    <input type="text" className={inputClass('contactFirstname')} value={deliveryData.contactFirstname} onChange={e => handleInputChange('contactFirstname', e.target.value)} placeholder="Prénom du contact" />
                    {errors.contactFirstname && <p className="tw-text-xs tw-text-red-500 tw-mt-1">{errors.contactFirstname}</p>}
                  </div>
                </div>
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                  <div>
                    <label className="tw-text-xs tw-text-gray-500 tw-mb-1 tw-block">Email <span className="tw-text-red-500">*</span></label>
                    <input type="email" className={inputClass('contactEmail')} value={deliveryData.contactEmail} onChange={e => handleInputChange('contactEmail', e.target.value)} placeholder="email@exemple.com" />
                    {errors.contactEmail && <p className="tw-text-xs tw-text-red-500 tw-mt-1">{errors.contactEmail}</p>}
                  </div>
                  <div>
                    <label className="tw-text-xs tw-text-gray-500 tw-mb-1 tw-block">Téléphone <span className="tw-text-red-500">*</span></label>
                    <input type="tel" className={inputClass('contactPhone')} value={deliveryData.contactPhone} onChange={e => handleInputChange('contactPhone', e.target.value)} placeholder="06 12 34 56 78" />
                    {errors.contactPhone && <p className="tw-text-xs tw-text-red-500 tw-mt-1">{errors.contactPhone}</p>}
                  </div>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
              <div className="tw-bg-gray-50 tw-px-4 tw-py-2.5 tw-border-b tw-border-gray-200">
                <h6 className="tw-text-sm tw-font-medium tw-flex tw-items-center tw-gap-2 tw-text-gray-600">
                  <MapPin className="tw-w-4 tw-h-4" />
                  Adresse de livraison
                </h6>
              </div>
              <div className="tw-p-4 tw-space-y-3">
                <div>
                  <label className="tw-text-xs tw-text-gray-500 tw-mb-1 tw-block">Nom de la société <span className="tw-text-red-500">*</span></label>
                  <input type="text" className={inputClass('companyName')} value={deliveryData.companyName} onChange={e => handleInputChange('companyName', e.target.value)} placeholder="Nom de la société" />
                  {errors.companyName && <p className="tw-text-xs tw-text-red-500 tw-mt-1">{errors.companyName}</p>}
                </div>
                <div>
                  <label className="tw-text-xs tw-text-gray-500 tw-mb-1 tw-block">Adresse <span className="tw-text-red-500">*</span></label>
                  <input type="text" className={inputClass('address')} value={deliveryData.address} onChange={e => handleInputChange('address', e.target.value)} placeholder="Adresse complète" />
                  {errors.address && <p className="tw-text-xs tw-text-red-500 tw-mt-1">{errors.address}</p>}
                </div>
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3">
                  <div>
                    <label className="tw-text-xs tw-text-gray-500 tw-mb-1 tw-block">Code postal <span className="tw-text-red-500">*</span></label>
                    <input type="text" className={inputClass('postalCode')} value={deliveryData.postalCode} onChange={e => handleInputChange('postalCode', e.target.value)} placeholder="00000" maxLength={5} />
                    {errors.postalCode && <p className="tw-text-xs tw-text-red-500 tw-mt-1">{errors.postalCode}</p>}
                  </div>
                  <div className="md:tw-col-span-2">
                    <label className="tw-text-xs tw-text-gray-500 tw-mb-1 tw-block">Ville <span className="tw-text-red-500">*</span></label>
                    <input type="text" className={inputClass('city')} value={deliveryData.city} onChange={e => handleInputChange('city', e.target.value)} placeholder="Ville" />
                    {errors.city && <p className="tw-text-xs tw-text-red-500 tw-mt-1">{errors.city}</p>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="tw-flex tw-justify-end tw-gap-3 tw-px-6 tw-py-4 tw-border-t tw-border-gray-200">
            <button
              type="button"
              className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-border-gray-300 tw-text-gray-600 hover:tw-bg-gray-50 tw-transition-colors"
              onClick={onCancel}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-bg-affinicar-secondary tw-text-white hover:tw-opacity-90 tw-transition-opacity"
            >
              <Check className="tw-w-4 tw-h-4" />
              Valider les informations
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
